import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useCustomerView } from '@/hooks/useCustomerView';
import { X, Eye, Printer, Package, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatJpWithAlias } from '@/utils/partDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Use a shared storage key with timestamp to sync across all tabs/windows
const LAST_CHECK_KEY = 'orderNotificationLastCheck';
const DISMISSED_KEY = 'dismissedOrderNotifications';
const SHOWN_KEY = 'shownOrderNotifications';

const OrderNotification = () => {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [viewOrderData, setViewOrderData] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const isPolling = useRef(false);
  const pollIntervalRef = useRef(null);
  
  // Check if user is admin or staff (masked by Customer View toggle)
  const { isCustomerView } = useCustomerView();
  const isAdminOrStaff = (user?.is_admin || user?.is_staff_user) && !isCustomerView;
  
  // Calculate basic price (price before 18% GST)
  const calculateBasicPrice = (taxPaidPrice) => {
    return (taxPaidPrice || 0) / 1.18;
  };
  
  // Generate a unique key for each order notification (order_id + timestamp)
  const getNotificationKey = (order) => {
    const timestamp = order.modified_at || order.last_edited_at || order.status_changed_at || order.created_at;
    return `${order.order_id}_${timestamp}`;
  };
  
  // Print order directly
  const printOrder = async (order) => {
    try {
      // If order doesn't have full details, fetch it first
      let fullOrder = order;
      if (!order.items || order.items.length === 0) {
        const response = await axios.get(`${API}/admin/orders/${order.order_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fullOrder = response.data;
      }
      
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(220, 38, 38);
      doc.text('JAI PARAS SPARES', 105, 15, { align: 'center' });
      
      // Order info
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Order: ${fullOrder.order_id}`, 14, 28);
      doc.text(`Customer: ${fullOrder.customer_name || '-'}`, 14, 34);
      doc.text(`Company: ${fullOrder.company_name || '-'}`, 14, 40);
      doc.text(`Date: ${new Date(fullOrder.created_at).toLocaleDateString('en-IN', {  })}`, 120, 28);
      // Only show discount if it's actually set (> 0)
      if (fullOrder.discount_percentage && fullOrder.discount_percentage > 0) {
        doc.text(`Discount: ${fullOrder.discount_percentage}%`, 120, 34);
      }
      
      // Table data
      const tableData = fullOrder.items.map((item, index) => {
        const taxPaidUnitPrice = item.discounted_price || item.price || item.mrp || 0;
        const basicPricePerUnit = calculateBasicPrice(taxPaidUnitPrice);
        const basicAmount = basicPricePerUnit * item.quantity;
        
        return [
          (index + 1).toString(),
          item.jp_part_no,
          item.part_name.length > 35 ? item.part_name.substring(0, 35) + '...' : item.part_name,
          item.quantity.toString(),
          basicPricePerUnit.toFixed(2),
          basicAmount.toFixed(2)
        ];
      });

      // Calculate totals
      const totalBasicAmount = fullOrder.items.reduce((sum, item) => {
        const taxPaidUnitPrice = item.discounted_price || item.price || item.mrp || 0;
        const basicPricePerUnit = calculateBasicPrice(taxPaidUnitPrice);
        return sum + (basicPricePerUnit * item.quantity);
      }, 0);
      
      const gstAmount = totalBasicAmount * 0.18;
      const totalAmount = totalBasicAmount + gstAmount;

      // Generate PDF table
      autoTable(doc, {
        startY: 48,
        head: [['S No', 'Jp Part No', 'Part Name', 'Quantity', 'Price', 'Amount']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [220, 38, 38], fontSize: 9, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center' },
          1: { cellWidth: 28 },
          2: { cellWidth: 70 },
          3: { cellWidth: 18, halign: 'center' },
          4: { cellWidth: 25, halign: 'right' },
          5: { cellWidth: 28, halign: 'right' }
        },
        margin: { left: 14, right: 14 }
      });

      // Footer totals
      const finalY = doc.lastAutoTable.finalY + 5;
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text('Total Basic Amount:', 120, finalY);
      doc.text(totalBasicAmount.toFixed(2), 182, finalY, { align: 'right' });
      
      doc.text('GST (18%):', 120, finalY + 6);
      doc.text(gstAmount.toFixed(2), 182, finalY + 6, { align: 'right' });
      
      doc.setDrawColor(0);
      doc.line(120, finalY + 9, 182, finalY + 9);
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('TOTAL AMOUNT:', 120, finalY + 15);
      doc.text(totalAmount.toFixed(2), 182, finalY + 15, { align: 'right' });

      // Auto-print
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Print error:', error);
    }
  };
  
  // Dismiss a notification (local + backend, cross-device sync)
  const dismissNotification = (order) => {
    const notifKey = getNotificationKey(order);
    setNotifications(prev => prev.filter(n => getNotificationKey(n) !== notifKey));

    // Local cache for instant filter + cross-tab event
    const dismissed = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
    if (!dismissed.includes(notifKey)) {
      dismissed.push(notifKey);
      if (dismissed.length > 500) {
        dismissed.splice(0, dismissed.length - 500);
      }
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
    }

    // Persist to backend so other devices for the same admin also suppress it.
    // Fire-and-forget — local cache already protects this device.
    if (token) {
      axios.post(`${API}/admin/order-notifications/dismiss`,
        { keys: [notifKey] },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch((e) => console.warn('Failed to persist dismissed notif:', e?.message));
    }
  };
  
  // View order - open inline detail dialog
  const viewOrder = async (order) => {
    dismissNotification(order);
    setLoadingOrder(true);
    try {
      const response = await axios.get(`${API}/admin/orders/${order.order_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewOrderData(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      setViewOrderData(order); // fallback to notification data
    } finally {
      setLoadingOrder(false);
    }
  };
  
  // Check for new and modified orders
  const checkNewOrders = useCallback(async () => {
    if (!token || !isAdminOrStaff || isPolling.current) return;
    
    isPolling.current = true;
    
    try {
      // Get last check time from localStorage (shared across all tabs).
      // 2026-05-29 (P0 — user-reported "fresh-device shows 51 toasts"):
      // first-poll lookback is now SHORT (5 minutes). The earlier
      // 7-day backfill caused every order from the past week to flood
      // the notification stack the moment the operator opened the app
      // on a new phone or cleared browser history. With the new
      // status='pending' backend filter + this short cursor, a new
      // login starts essentially clean — only orders received within
      // the last 5 minutes (and still pending) will surface. Polling
      // thereafter still catches every new order in real-time.
      let lastCheck = localStorage.getItem(LAST_CHECK_KEY);
      if (!lastCheck) {
        lastCheck = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      }
      
      const response = await axios.get(`${API}/admin/orders/new?since=${encodeURIComponent(lastCheck)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.orders && response.data.orders.length > 0) {
        // Cross-device: dismissals are the ONLY filter. SHOWN_KEY was
        // local-only (caused a fresh login on a new device to see
        // every order again — even ones the user had dismissed
        // elsewhere) so we removed it. The server-side DISMISSED list
        // is fetched on mount and merged into local cache.
        const dismissed = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');

        const newNotifications = response.data.orders.filter(order => {
          const notifKey = getNotificationKey(order);
          return !dismissed.includes(notifKey);
        });

        if (newNotifications.length > 0) {
          // Add to current notifications (avoid duplicates)
          setNotifications(prev => {
            const existingKeys = prev.map(n => getNotificationKey(n));
            const toAdd = newNotifications.filter(n => !existingKeys.includes(getNotificationKey(n)));
            return [...toAdd, ...prev];
          });
          
          // Play notification sound
          try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {});
          } catch (e) {}
        }
      }
      
      // Update last check time
      localStorage.setItem(LAST_CHECK_KEY, new Date().toISOString());
      
    } catch (error) {
      console.error('Error checking new orders:', error);
    } finally {
      isPolling.current = false;
    }
  }, [token, isAdminOrStaff]);
  
  // Initialize and set up polling
  useEffect(() => {
    if (!isAdminOrStaff || !token) return;

    // Cross-device hydration: pull this admin's already-dismissed keys from
    // the backend and merge into local cache before the first poll, so that
    // a notification dismissed on another device is suppressed here too.
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/order-notifications/dismissed`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        const remote = Array.isArray(res.data?.keys) ? res.data.keys : [];
        if (remote.length) {
          const local = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
          const merged = Array.from(new Set([...local, ...remote]));
          if (merged.length > 500) merged.splice(0, merged.length - 500);
          localStorage.setItem(DISMISSED_KEY, JSON.stringify(merged));
          // Drop anything currently rendered that matches the remote set
          setNotifications(prev => prev.filter(n => !remote.includes(getNotificationKey(n))));
        }
      } catch (e) {
        // Non-fatal: localStorage still works
        console.warn('Failed to hydrate dismissed notifs:', e?.message);
      } finally {
        if (!cancelled) checkNewOrders();
      }
    })();

    // Check every 30 seconds — reduced from 5s to prevent network overload
    pollIntervalRef.current = setInterval(checkNewOrders, 30000);
    
    // Also listen for storage changes (when another tab updates)
    const handleStorageChange = (e) => {
      if (e.key === DISMISSED_KEY) {
        // Another tab dismissed a notification - remove it from our list too
        const dismissed = JSON.parse(e.newValue || '[]');
        setNotifications(prev => prev.filter(n => !dismissed.includes(getNotificationKey(n))));
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      cancelled = true;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAdminOrStaff, token, checkNewOrders]);
  
  // Clear shown notifications on component unmount (but keep dismissed)
  useEffect(() => {
    return () => {
      // On unmount, move current notifications to dismissed so they don't re-appear
      const currentNotifKeys = notifications.map(n => getNotificationKey(n));
      if (currentNotifKeys.length > 0) {
        const dismissed = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
        const updated = [...new Set([...dismissed, ...currentNotifKeys])];
        if (updated.length > 500) {
          updated.splice(0, updated.length - 500);
        }
        localStorage.setItem(DISMISSED_KEY, JSON.stringify(updated));
        // Also persist to backend (best-effort) so other devices stay in sync.
        if (token) {
          axios.post(`${API}/admin/order-notifications/dismiss`,
            { keys: currentNotifKeys },
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => {});
        }
      }
    };
  }, [notifications, token]);
  
  // Clear all currently visible notifications + persist for cross-device
  const clearAllNotifications = useCallback(async () => {
    const keys = notifications.map(getNotificationKey);
    setNotifications([]);
    // Update local dismissed cache so the next poll doesn't bring them back.
    const dismissed = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
    const merged = Array.from(new Set([...dismissed, ...keys]));
    if (merged.length > 500) merged.splice(0, merged.length - 500);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(merged));
    if (token) {
      // Snapshot every recent order's notification key on the server
      // so a different device for the same admin also stops showing
      // them. Fire-and-forget — local cache already protects this one.
      axios.post(`${API}/admin/order-notifications/clear-all`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch((e) => console.warn('Failed clear-all:', e?.message));
    }
  }, [notifications, token]);

  // Don't render if not admin/staff or no notifications
  // Keep rendering if dialog is open OR loading order data (viewOrder dismisses notification before API call completes)
  if (!isAdminOrStaff || (notifications.length === 0 && !viewOrderData && !loadingOrder)) {
    return null;
  }
  
  return (
    <div
      className="fixed bottom-4 left-4 z-[100000] flex flex-col-reverse gap-3 max-w-sm pointer-events-none max-h-[85vh]"
      data-overlay-immune="true"
      data-testid="order-notifications"
    >
      {/* Footer pill — ALWAYS visible at the very bottom (anchored just
          above the screen edge) so the operator can hit Clear All even
          when 50+ notifications are stacked above and the screen is
          short. This is the user-reported "I can't see Clear All on
          my phone with 51 toasts" fix. */}
      {notifications.length >= 1 && (
        <div className="flex justify-between items-center gap-2 pointer-events-auto">
          {notifications.length > 5 && (
            <span
              className="text-[11px] font-medium text-white/95 bg-gray-800/85 backdrop-blur-md rounded-full px-3 py-1"
              data-testid="order-notifications-more-count"
            >
              +{notifications.length - 5} more
            </span>
          )}
          <button
            onClick={clearAllNotifications}
            className="ml-auto text-[12px] font-semibold text-white bg-red-600 hover:bg-red-700 rounded-full px-4 py-1.5 shadow-lg transition active:scale-95"
            data-testid="order-notifications-clear-all-btn"
          >
            Clear All{notifications.length > 1 ? ` (${notifications.length})` : ''}
          </button>
        </div>
      )}
      {notifications.slice(0, 5).map((order) => {
        const isModified = order.notification_type === 'modified';
        
        return (
          <div 
            key={getNotificationKey(order)}
            className={`bg-white border-2 ${isModified ? 'border-orange-500' : 'border-red-500'} rounded-lg shadow-2xl p-4 animate-in slide-in-from-left duration-300 relative pointer-events-auto`}
            data-testid={`order-notification-${order.order_id}`}
          >            {/* Close button */}
            <button
              onClick={() => dismissNotification(order)}
              className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              data-testid={`dismiss-notification-${order.order_id}`}
            >
              <X className="w-4 h-4" />
            </button>
            
            {/* Header */}
            <div className="flex items-center gap-2 mb-3 pr-6">
              <div className={`p-2 ${isModified ? 'bg-orange-100' : 'bg-red-100'} rounded-full`}>
                {isModified ? (
                  <Edit className="w-5 h-5 text-orange-600" />
                ) : (
                  <Package className="w-5 h-5 text-red-600" />
                )}
              </div>
              <div>
                <p className={`font-bold ${isModified ? 'text-orange-600' : 'text-red-600'} text-sm uppercase tracking-wide`}>
                  {isModified ? 'Order Modified' : 'Order Received'}
                </p>
                <p className="text-xs text-gray-500 font-mono">{order.order_id}</p>
              </div>
            </div>
            
            {/* Customer Name */}
            <div className="mb-4">
              <p className="font-semibold text-gray-900 text-lg">
                {order.customer_name || order.company_name || 'Customer'}
              </p>
              {order.company_name && order.customer_name && (
                <p className="text-sm text-gray-500">{order.company_name}</p>
              )}
              <p className="text-sm font-medium text-green-600 mt-1">
                ₹{order.final_amount?.toLocaleString('en-IN') || '-'}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={() => viewOrder(order)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-9"
                size="sm"
                data-testid={`view-order-${order.order_id}`}
              >
                <Eye className="w-4 h-4 mr-1" />
                View Order
              </Button>
              <Button
                onClick={() => printOrder(order)}
                variant="outline"
                className="flex-1 border-purple-500 text-purple-600 hover:bg-purple-50 h-9"
                size="sm"
                data-testid={`print-order-${order.order_id}`}
              >
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
            </div>
          </div>
        );
      })}

      {/* Order Detail Dialog */}
      <Dialog open={!!viewOrderData} onOpenChange={() => setViewOrderData(null)}>
        <DialogContent className="max-w-3xl bg-white border-2 border-gray-300 shadow-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              Order Details: {viewOrderData?.order_id}
            </DialogTitle>
          </DialogHeader>
          {loadingOrder ? (
            <div className="py-8 text-center text-gray-500">Loading order details...</div>
          ) : viewOrderData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 font-medium">Company Name</p>
                  <p className="font-semibold text-gray-900">{viewOrderData.company_name || viewOrderData.customer_name || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Phone</p>
                  <p className="font-semibold text-gray-900">{viewOrderData.customer_phone || viewOrderData.customer_mobile || '-'}</p>
                </div>
                {viewOrderData.gst_number && (
                  <div>
                    <p className="text-gray-500 font-medium">GST Number</p>
                    <p className="font-semibold text-gray-900">{viewOrderData.gst_number}</p>
                  </div>
                )}
                {viewOrderData.customer_email && (
                  <div>
                    <p className="text-gray-500 font-medium">Email</p>
                    <p className="font-semibold text-gray-900">{viewOrderData.customer_email}</p>
                  </div>
                )}
                {viewOrderData.discount_percentage > 0 && (
                  <div>
                    <p className="text-gray-500 font-medium">Discount</p>
                    <p className="font-semibold text-green-600">{viewOrderData.discount_percentage}%</p>
                  </div>
                )}
                {viewOrderData.shipping_address && (
                  <div className="col-span-2">
                    <p className="text-gray-500 font-medium">Shipping Address</p>
                    <p className="font-semibold text-gray-900">{viewOrderData.shipping_address}</p>
                  </div>
                )}
                {viewOrderData.notes && (
                  <div className="col-span-2">
                    <p className="text-gray-500 font-medium">Notes</p>
                    <p className="font-semibold text-gray-900">{viewOrderData.notes}</p>
                  </div>
                )}
              </div>
              
              {viewOrderData.items?.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-bold text-gray-900 mb-2">Items ({viewOrderData.items.length})</h4>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-gray-700 font-semibold">Part No</TableHead>
                        <TableHead className="text-gray-700 font-semibold">Name</TableHead>
                        <TableHead className="text-gray-700 font-semibold text-center">Qty</TableHead>
                        <TableHead className="text-gray-700 font-semibold text-right">Price</TableHead>
                        <TableHead className="text-gray-700 font-semibold text-right">Subtotal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewOrderData.items.map((item, idx) => {
                        const unitPrice = item.discounted_price || item.price || item.mrp || 0;
                        const subtotal = item.subtotal || (unitPrice * item.quantity);
                        return (
                          <TableRow key={idx} className="border-b border-gray-100">
                            <TableCell className="font-mono text-sm text-gray-900">{formatJpWithAlias(item)}</TableCell>
                            <TableCell className="text-gray-900 text-sm">{item.part_name}</TableCell>
                            <TableCell className="text-gray-900 text-center">{item.quantity}</TableCell>
                            <TableCell className="text-green-600 font-medium text-right">
                              Rs.{unitPrice.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-gray-900 font-semibold text-right">
                              Rs.{subtotal.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              <div className="border-t border-gray-200 pt-4 flex justify-between items-center">
                <Badge className={`${viewOrderData.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                  {viewOrderData.status}
                </Badge>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">
                    Total: Rs.{(viewOrderData.final_amount || viewOrderData.total_amount || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderNotification;
